import { Dialog, DropdonwFlags, DropdownMenu, Loading } from '@/components';
import React, { ReactNode } from 'react';
import { ActionProps } from '@/components/Dialog';
import { Container } from 'reactstrap';
import { SimpleSelect } from '@/components/SimpleSelect';
import Page from '@/model/Page';
import Event from '@/model/Event';
import FilterVector from '@/assets/images/svg/FilterVector';
import { ColumnImage, ColumnStatus, CustomTable } from '@/components/Table';
import Pagination from '@/components/Utils/Pagination';
import EventStatus from '@/model/EventStatus';
import { colors } from '@/styles/colors';
import dayjs from 'dayjs';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import { ReactComponent as EventAction } from '@/assets/images/svg/eventAction.svg';
import { ReactComponent as Copy } from '@/assets/images/svg/copy.svg';
import { ReactComponent as Report } from '@/assets/images/svg/report.svg';
import eye from '@/assets/images/login/eye.png';
import { ActionProps as ActionPropsMenu } from '@/components/DropdownMenu';
import { useHistory } from 'react-router-dom';
import { FilterContent } from '../../../components/FilterContent';
import { columns, dataEventType, paginationSelect, ShouldShowModal, States } from '../../../types';

interface StateProps {
  state: States;
  title: string | ReactNode;
  visible: boolean;
  shouldShowModal: ShouldShowModal;
  page: Page<Event, Event>;
  menuList: string[];
  formData: FormData;
  formErrors: FormErrors;
}

interface DispatchProps {
  onToggle: () => void;
  onClearFilter: () => void;
  onFilter: () => void;
  onClone: (eventId: string) => void;
  onChangePageSize: (pageSize: number) => void;
  onShouldShowModal: ({
    value,
    newTitleModal,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | React.ReactNode;
  }) => void;
  onPaginationChange: (newPage: number) => void;
  onChangeFormInput: OnChangeFormInput;
}

type Props = StateProps & DispatchProps;

export const FinishedEventsContainer: React.FC<Props> = ({
  state,
  title,
  visible,
  onToggle,
  shouldShowModal,
  onClearFilter,
  onFilter,
  onClone,
  onChangePageSize,
  page,
  menuList,
  onShouldShowModal,
  onPaginationChange,
  formData,
  formErrors,
  onChangeFormInput,
}): JSX.Element => {
  const history = useHistory();
  const renderActionDialogToCancelFilter: ActionProps = {
    title: 'Limpar',
    onClick: (): void => onClearFilter(),
    theme: 'noneBorder',
  };

  const changeColorColumn = (status: EventStatus): string => {
    let color = colors.grey;
    switch (status) {
      case EventStatus.DRAFT:
        color = colors.lightBlue;
        break;
      case EventStatus.PENDING:
        color = colors.darkRed;
        break;
      case EventStatus.ACTIVE:
        color = colors.green;
        break;
      case EventStatus.REFUSED:
        color = colors.yellow;
        break;
      case EventStatus.BLOCKED:
        color = colors.red;
        break;
      case EventStatus.FINISHED:
        color = colors.lightGreen;
        break;
      case EventStatus.INACTIVE:
        color = colors.orange;
        break;
      case EventStatus.COMPLETED:
        color = colors.grey;
        break;
      default:
        color = colors.grey;
        break;
    }
    return color;
  };

  const foundPermission = (value: string): boolean => {
    let found = false;
    if (menuList && menuList.length > 0) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < menuList.length; i++) {
        if (menuList[i] === value) {
          found = true;
          break;
        }
      }
    }
    return found;
  };

  const mountMenuActions = (event: Event): ActionPropsMenu[] => {
    const list: ActionPropsMenu[] = [];
    if (menuList && menuList.length > 0) {
      if (foundPermission('Detalhe')) {
        list.push({
          title: 'Detalhe',
          icon: (
            <img
              className="d-flex justify-content-center align-items-center"
              src={eye}
              style={{ width: '20px' }}
            />
          ),
          action: () => {
            history.push({
              pathname: `/dashboard/event/finished/detail/${event.id}`,
            });
          },
        });
      }
      if (foundPermission('Clonar')) {
        list.push({
          title: 'Clonar',
          icon: <Copy style={{ transform: 'scale(0.9)', marginLeft: '-1px' }} />,
          action: () => {
            onClone(event.id);
          },
        });
      }
      if (foundPermission('Relatórios')) {
        list.push({
          title: 'Relatórios',
          icon: <Report />,
          action: () => {
            history.push({
              pathname: `/dashboard/event/reports/${event.id}/false`,
            });
          },
        });
      }
    }
    return list;
  };

  const dataTablePos = page.list
    ? page.list.map(event => ({
        id: event.id,
        image: (
          <ColumnStatus justify="right" statusColor={String(changeColorColumn(event.eventStatus))}>
            <ColumnImage srcImage={event.imageBase64} />
          </ColumnStatus>
        ),
        name: (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div>{event.name}</div>
            {
              <DropdonwFlags
                className="mt-2"
                dataColumn={dataEventType.filter(value => value.id === event.eventType)}
              />
            }
          </div>
        ),
        city: `${event.address.city}/${event.address.state}`,
        startDate:
          event.startDate === null
            ? '-----'
            : // eslint-disable-next-line no-useless-concat
              `${dayjs(event.startDate).format('DD/MM/YYYY')} às ${String(event.startDate)
                .split('T')[1]
                .slice(0, 5)}`,
        endDate:
          event.endDate === null
            ? '-----'
            : `${dayjs(event.endDate).format('DD/MM/YYYY')} às ${String(event.endDate)
                .split('T')[1]
                .slice(0, 5)}`,
        actions: <DropdownMenu title={<EventAction />} actions={mountMenuActions(event)} />,
      }))
    : [];

  return (
    <>
      <Loading isVisible={state === States.loading} />
      <Dialog
        title={title}
        visible={visible}
        onClose={onToggle}
        footerBorder={'top'}
        position={shouldShowModal === ShouldShowModal.filter ? 'right' : 'center'}
        isContentWithCard={shouldShowModal !== ShouldShowModal.filter}
        actions={[
          {
            [ShouldShowModal.filter]: renderActionDialogToCancelFilter,
          }[shouldShowModal],
          {
            [ShouldShowModal.filter]: {
              title: 'Aplicar',
              onClick: onFilter,
            },
          }[shouldShowModal],
        ]}
      >
        {
          {
            [ShouldShowModal.filter]: (
              <FilterContent
                formData={formData}
                formErrors={formErrors}
                onChangeFormInput={onChangeFormInput}
              />
            ),
          }[shouldShowModal]
        }
      </Dialog>
      <Container className="mainContainer" fluid={true}>
        <div
          className="d-flex justify-content-between event-page-title-container"
          style={{ paddingBottom: '30px' }}
        >
          <div className="pageTitle event-page-title" style={{ display: 'grid' }}>
            <h5 className="pageTitle" style={{ marginBottom: '1px' }}>
              Todos os eventos finalizados
            </h5>
          </div>
          <div className="button-filter-container event-button-filter">
            <div className="d-flex event-filter-container">
              <div className="select-label-container" style={{ marginLeft: '15px' }}>
                <SimpleSelect
                  name={'Exibir'}
                  value={page.pageSize}
                  options={paginationSelect}
                  placeholder="10 por página"
                  label="Exibir:"
                  onChange={e => {
                    // eslint-disable-next-line no-unsafe-optional-chaining
                    onChangePageSize(Number(e?.value));
                  }}
                />
              </div>
              <div className="filter-container m-filter">
                <div
                  className="filter-content"
                  onClick={(): void =>
                    onShouldShowModal({
                      value: ShouldShowModal.filter,
                      newTitleModal: '',
                    })
                  }
                >
                  <FilterVector />
                </div>
              </div>
            </div>
          </div>
        </div>
        <CustomTable
          columns={columns}
          data={dataTablePos}
          numberRowsPerPage={10}
          theme="primary"
          progressPending={false}
        />
        <Pagination
          currentPage={page.page}
          totalCount={page.total}
          pageSize={page.pageSize}
          onPageChange={onPaginationChange}
          total={page.total}
        />
      </Container>
    </>
  );
};
